import type { OrgPreset } from '@/lib/orgPresets';

const FONTS_LINK_ID = 'org-brand-fonts';
const FAVICON_ATTR = 'data-org-brand-favicon';

/**
 * Veste o documento com a marca de um preset: `data-brand` no <html> (os tokens de cor, fonte
 * e raio ficam em src/styles/themes/), folha de fontes da marca e favicon. Devolve a função
 * que desfaz tudo.
 *
 * Usado pelo shell do CRM (`useApplyOrgBranding`, org logada) e pelas páginas públicas de
 * agendamento (`usePublicBrand`, marca vinda da página). Os <link rel=icon> do index.html
 * ficam no lugar, só desativados, e voltam no cleanup.
 */
export function applyBrandToDocument(preset: OrgPreset): () => void {
  const root = document.documentElement;
  root.dataset.brand = preset.brandAttr;

  let fonts = document.getElementById(FONTS_LINK_ID) as HTMLLinkElement | null;
  if (!fonts) {
    fonts = document.createElement('link');
    fonts.id = FONTS_LINK_ID;
    fonts.rel = 'stylesheet';
    document.head.appendChild(fonts);
  }
  fonts.href = preset.fontsHref;

  const originais = Array.from(
    document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"], link[rel="apple-touch-icon"]'),
  ).filter((l) => !l.hasAttribute(FAVICON_ATTR));
  const hrefsOriginais = originais.map((l) => l.getAttribute('href'));
  originais.forEach((l) => l.removeAttribute('href'));
  const favicon = document.createElement('link');
  favicon.rel = 'icon';
  favicon.type = 'image/svg+xml';
  favicon.href = preset.faviconHref;
  favicon.setAttribute(FAVICON_ATTR, '');
  document.head.appendChild(favicon);

  return () => {
    delete root.dataset.brand;
    document.getElementById(FONTS_LINK_ID)?.remove();
    favicon.remove();
    originais.forEach((l, i) => {
      const href = hrefsOriginais[i];
      if (href) l.setAttribute('href', href);
    });
  };
}
